import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { TrendingUp, TrendingDown, Zap, AlertTriangle, BarChart3, MapPin, FileText, Calculator, DollarSign, Building2, Fuel, ShoppingCart, Wallet, PiggyBank, Users } from 'lucide-react';
import { Button } from '../ui/Button';
import { BrokerConnectionModal } from '../valuation/BrokerConnectionModal';
import { ValuationProperty } from './PreviouslyValuedProperties';
import { useSupabase } from '../../hooks/useSupabase';
import { retrieveValuationById } from '../../services/valuationService';
import {
  fetchIndustryBenchmarks,
  OperationalComparison,
  IndustryBenchmark
} from '../../services/comparisonService';
import { ValuationData } from '../../types/valuation';
import SwotQuadrantChart, { SwotItem } from '../charts/SwotQuadrantChart';
import TaxDepreciationDemo from '../demo/TaxDepreciationDemo';
import LoanCalculatorDemo from '../demo/LoanCalculatorDemo';
import { getPropertySatelliteImageUrl } from '../../utils/googleMaps';
import { calculateEntrepreneurialMultiplier, defaultSynergyAnswers } from '../../types/entrepreneurial-synergy';
import { calculateFuelSupplyBenefit } from '../../utils/fuelSupplyBenefit';

interface ComparisonData {
  metricName: string;
  userValue: number;
  average: number;
  median: number;
  percentile25: number;
  percentile75: number;
  percentileRank: number;
  isMonetary: boolean;
  unit: string;
  desiredTrend: 'up' | 'down';
}

interface PerformanceAnalysisProps {
  valuationId?: string;
}

// Cost table: asset effective life and condition → years depreciated
const COST_TABLE: Record<string, { effectiveLife: number; depYears: Record<string, number> }> = {
  Car_Wash:          { effectiveLife: 40, depYears: { New: 1, Excellent: 1, Good: 5,  Average: 10, Fair: 15 } },
  Car_Wash_Equipment:{ effectiveLife: 15, depYears: { New: 1, Excellent: 1, Good: 5,  Average: 7,  Fair: 10 } },
  Convenience_Store: { effectiveLife: 40, depYears: { New: 1, Excellent: 1, Good: 6,  Average: 10, Fair: 15 } },
  FFE:               { effectiveLife: 15, depYears: { New: 1, Excellent: 1, Good: 3,  Average: 6,  Fair: 10 } },
  Food_Booth:        { effectiveLife: 40, depYears: { New: 1, Excellent: 1, Good: 5,  Average: 10, Fair: 15 } },
  Fueling_Canopy:    { effectiveLife: 25, depYears: { New: 1, Excellent: 1, Good: 4,  Average: 7,  Fair: 8  } },
  Fueling_Equipment: { effectiveLife: 15, depYears: { New: 1, Excellent: 1, Good: 3,  Average: 6,  Fair: 9  } },
  Kiosk:             { effectiveLife: 40, depYears: { New: 1, Excellent: 1, Good: 6,  Average: 10, Fair: 15 } },
  Service_Station:   { effectiveLife: 40, depYears: { New: 1, Excellent: 1, Good: 7,  Average: 10, Fair: 15 } },
  Site_Improvements: { effectiveLife: 21, depYears: { New: 1, Excellent: 1, Good: 3,  Average: 5,  Fair: 8  } },
  UST:               { effectiveLife: 15, depYears: { New: 1, Excellent: 1, Good: 4,  Average: 7,  Fair: 12 } },
};

// Incentive rates (mid values)
const INDIRECT_COSTS_MID = 0.25;
const ENTREPRENEURIAL_INCENTIVE_MID = 0.20;

export default function PerformanceAnalysis({ valuationId: propValuationId }: PerformanceAnalysisProps) {
  const { id: paramId } = useParams<{ id: string }>();
  const valuationId = propValuationId || paramId;
  const { supabase } = useSupabase();

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [valuation, setValuation] = useState<ValuationProperty | null>(null);
  const [comparisonData, setComparisonData] = useState<ComparisonData[]>([]);
  const [industryBenchmarks, setIndustryBenchmarks] = useState<IndustryBenchmark[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [swotItems, setSwotItems] = useState<SwotItem[]>([]);
  const [activeBreakdownTab, setActiveBreakdownTab] = useState<'detailed' | 'input'>('detailed');
  const [brokerModalOpen, setBrokerModalOpen] = useState(false);

  // --- Helper functions ---

  function getActualTotalValue(data: ValuationData): number {
    const apiResult = data.apiResponse?.result;
    if (apiResult?.total_station_value) return apiResult.total_station_value;

    // Sum components as fallback
    let total = 0;
    if (apiResult?.predicted_land_value) total += apiResult.predicted_land_value;
    if (apiResult?.predicted_business_value) total += apiResult.predicted_business_value;
    if (apiResult?.depreciated_assets) {
      Object.values(apiResult.depreciated_assets).forEach((asset: any) => {
        if (asset?.Depreciated_Cost) total += asset.Depreciated_Cost;
      });
    }
    return total || fallbackEstimate(data);
  }

  function fallbackEstimate(data: ValuationData): number {
    let value = 500000;
    value += (data.propertySize || 0) * 100;
    value += (data.lotSize || 0) * 50;
    if (data.annualGallons) value += data.annualGallons * 0.5;
    if (data.storeRevenue) value += data.storeRevenue * 1.2;
    return Math.round(value);
  }

  function calculateCostToCompete(data: ValuationData): number {
    const apiResult = data.apiResponse?.result;
    if (!apiResult?.depreciated_assets) return 0;

    const userAssets = (data.assets || {}) as Record<string, { condition: string; size: number } | undefined>;
    let totalRCN = 0;

    Object.entries(apiResult.depreciated_assets).forEach(([key, assetData]: [string, any]) => {
      if (!assetData?.Depreciated_Cost || assetData.Depreciated_Cost <= 0) return;

      const costEntry = COST_TABLE[key];
      if (!costEntry) {
        // Unknown asset — fall back to using depreciated cost as-is
        totalRCN += assetData.Depreciated_Cost;
        return;
      }

      const userCondition = userAssets[key]?.condition || 'Average';
      const yearsDep = costEntry.depYears[userCondition] ?? costEntry.depYears['Average'];
      const retainedPct = 1 - (yearsDep / costEntry.effectiveLife);

      if (retainedPct > 0) {
        totalRCN += assetData.Depreciated_Cost / retainedPct;
      }
    });

    const landValue = apiResult.predicted_land_value || 0;

    // Cost to Compete = (RCN + Land + Indirect Costs) less Entrepreneurial Incentive
    const grossRCN = totalRCN + landValue;
    const withIndirect = grossRCN * (1 + INDIRECT_COSTS_MID);
    return withIndirect * (1 - ENTREPRENEURIAL_INCENTIVE_MID);
  }

  function calculateEntrepreneurialValue(data: ValuationData, baseValue: number): { value: number; uplift: number; factorCount: number } {
    const synergyAnswers = data.entrepreneurialSynergy || defaultSynergyAnswers;
    const { multiplier, uplift, factors } = calculateEntrepreneurialMultiplier(synergyAnswers);
    return { value: baseValue * multiplier, uplift, factorCount: factors.length };
  }

  function getDisplayValue(data: ValuationData): number {
    const baseValue = getActualTotalValue(data);

    // Apply fuel supply benefit if applicable
    const apiResult = data.apiResponse?.result;
    const grossProfit = apiResult?.gross_profit || 0;
    const annualOpEx = data.isAnnual ? (data.operatingExpenses || 0) : (data.operatingExpenses || 0) * 12;
    const noi = grossProfit - annualOpEx;

    const fuelSupplyBenefit = calculateFuelSupplyBenefit(
      baseValue, grossProfit, noi,
      data.brandAffiliation || 'unbranded',
      data.fuelSupplyYears || 0
    );

    return fuelSupplyBenefit.qualifies ? fuelSupplyBenefit.adjustedValue : baseValue;
  }

  function calculateSalesPerSF(data: ValuationData): number {
    if (!data.propertySize || data.propertySize === 0) return 0;
    return (data.storeRevenue || 0) / data.propertySize;
  }

  function calculateTotalGrossProfit(data: ValuationData): number {
    if (data.totalGrossProfit) return data.totalGrossProfit;
    const fuelProfit = (data.annualGallons || 0) * (data.fuelMargin || 0);
    const storeProfit = (data.storeRevenue || 0) * (data.storeMargin || 0.3);
    const carWashProfit = (data.carWashRevenue || 0) * (data.carWashMargin || 0.7);
    const foodServiceProfit = (data.foodServiceRevenue || 0) * (data.foodServiceMargin || 0.4);
    return fuelProfit + storeProfit + carWashProfit + foodServiceProfit;
  }

  // Returns the user's position in the value distribution (0–99). Always
  // reflects "where the value sits", not "how good it is" — the desiredTrend
  // / inversed flag is applied later by getPerformanceIndicator and the chart
  // gradient direction. The previous version inverted the ratio for inversed
  // metrics, which produced "low expenses → 99th percentile → Below Average"
  // (wrong). The `inversed` arg is retained only to control the default for
  // missing data so the fallback still flags as Below Average rather than
  // accidentally Excellent.
  function calculatePercentileRank(value: number, average: number, inversed: boolean = false): number {
    if (value === 0 || average === 0) return inversed ? 75 : 25;
    const ratio = value / average;
    let percentile = 50 + ((ratio - 1) * 30);
    return Math.min(99, Math.max(1, Math.round(percentile)));
  }

  function getPerformanceIndicator(percentile: number, inversed: boolean): string {
    if (inversed) {
      if (percentile <= 25) return 'Excellent';
      if (percentile <= 50) return 'Good';
      if (percentile <= 75) return 'Average';
      return 'Below Average';
    } else {
      if (percentile >= 75) return 'Excellent';
      if (percentile >= 50) return 'Good';
      if (percentile >= 25) return 'Average';
      return 'Below Average';
    }
  }

  function getPercentileColor(percentile: number, inversed: boolean): string {
    const indicator = getPerformanceIndicator(percentile, inversed);
    switch (indicator) {
      case 'Excellent': return 'bg-green-500';
      case 'Good': return 'bg-blue-500';
      case 'Average': return 'bg-yellow-500';
      default: return 'bg-red-500';
    }
  }

  function generateFallbackComparisonMetrics(data: ValuationData): ComparisonData[] {
    const totalGP = calculateTotalGrossProfit(data);
    const opex = data.operatingExpenses || 0;
    return [
      {
        metricName: 'Annual Fuel Volume',
        userValue: data.annualGallons || 0,
        average: 1620000, median: 1580000, percentile25: 1200000, percentile75: 1900000,
        percentileRank: calculatePercentileRank(data.annualGallons || 0, 1620000),
        isMonetary: false, unit: 'gallons', desiredTrend: 'up'
      },
      {
        metricName: 'Fuel Profit Margin',
        userValue: data.fuelMargin || 0,
        average: 0.28, median: 0.27, percentile25: 0.22, percentile75: 0.33,
        percentileRank: calculatePercentileRank(data.fuelMargin || 0, 0.28),
        isMonetary: true, unit: '$/gal', desiredTrend: 'up'
      },
      {
        metricName: 'C-Store Sales',
        userValue: data.storeRevenue || 0,
        average: 820000, median: 790000, percentile25: 600000, percentile75: 980000,
        percentileRank: calculatePercentileRank(data.storeRevenue || 0, 820000),
        isMonetary: true, unit: '$', desiredTrend: 'up'
      },
      {
        metricName: 'C-Store Sales per SF',
        userValue: calculateSalesPerSF(data),
        average: 320, median: 310, percentile25: 260, percentile75: 370,
        percentileRank: calculatePercentileRank(calculateSalesPerSF(data), 320),
        isMonetary: true, unit: '$/SF', desiredTrend: 'up'
      },
      {
        metricName: 'Total Gross Profit',
        userValue: totalGP,
        average: 520000, median: 500000, percentile25: 400000, percentile75: 620000,
        percentileRank: calculatePercentileRank(totalGP, 520000),
        isMonetary: true, unit: '$', desiredTrend: 'up'
      },
      {
        metricName: 'Operating Expenses',
        userValue: opex,
        average: 480000, median: 460000, percentile25: 380000, percentile75: 560000,
        percentileRank: calculatePercentileRank(opex, 480000, true),
        isMonetary: true, unit: '$', desiredTrend: 'down'
      }
    ];
  }

  // SWOT generation
  function generateSwot(data: ValuationData, comparisons: ComparisonData[]): Record<string, string[]> {
    const swot: Record<string, string[]> = { strengths: [], weaknesses: [], opportunities: [], threats: [] };

    comparisons.forEach(metric => {
      if (metric.desiredTrend === 'up' && metric.percentileRank >= 75) {
        swot.strengths.push(`${metric.metricName} performs in top 25% of market (${metric.percentileRank}th percentile)`);
      } else if (metric.desiredTrend === 'down' && metric.percentileRank <= 25) {
        swot.strengths.push(`${metric.metricName} is well-controlled, outperforming 75% of competitors`);
      }
      if (metric.desiredTrend === 'up' && metric.percentileRank <= 25) {
        swot.weaknesses.push(`${metric.metricName} underperforms market average (${metric.percentileRank}th percentile)`);
      } else if (metric.desiredTrend === 'down' && metric.percentileRank >= 75) {
        swot.weaknesses.push(`${metric.metricName} is higher than desired, exceeding 75% of market`);
      }
    });

    if (data.fuelMargin && data.fuelMargin >= 0.35)
      swot.strengths.push(`Strong fuel margins at $${data.fuelMargin.toFixed(2)} per gallon`);
    if (data.storeRevenue && data.storeRevenue >= 800000)
      swot.strengths.push(`High-performing convenience store with $${(data.storeRevenue/1000).toFixed(0)}K annual revenue`);
    if (data.annualGallons && data.annualGallons >= 1500000)
      swot.strengths.push(`High fuel volume location with ${(data.annualGallons/1000000).toFixed(1)}M gallons annually`);
    if (data.trafficCount && data.trafficCount >= 12000)
      swot.strengths.push(`Excellent traffic exposure with ${data.trafficCount.toLocaleString()} vehicles per day`);

    if (data.fuelMargin && data.fuelMargin < 0.25)
      swot.weaknesses.push(`Below-market fuel margins at $${data.fuelMargin.toFixed(2)} per gallon`);
    if (data.yearBuilt && data.yearBuilt < 2000)
      swot.weaknesses.push(`Aging infrastructure built in ${data.yearBuilt}`);

    if (data.trafficCount && data.trafficCount >= 10000 && data.storeRevenue && data.storeRevenue < 600000)
      swot.opportunities.push(`High traffic count suggests untapped c-store revenue potential`);
    if (!data.hasCarWash && data.trafficCount && data.trafficCount >= 8000)
      swot.opportunities.push(`Car wash installation could capitalize on ${data.trafficCount.toLocaleString()} daily traffic`);
    if (!data.hasFoodService && data.storeRevenue && data.storeRevenue >= 400000)
      swot.opportunities.push(`Food service addition could boost convenience store performance`);

    swot.threats.push(`Rising fuel costs could compress margins`);
    swot.threats.push(`Electric vehicle adoption may reduce long-term fuel demand`);
    if (data.fuelMargin && data.fuelMargin >= 0.40)
      swot.threats.push(`High fuel margins may be vulnerable to competitive pricing pressure`);

    // Ensure minimums
    if (swot.strengths.length === 0) { swot.strengths.push('Established market presence'); swot.strengths.push('Operational fuel and convenience retail business'); }
    if (swot.weaknesses.length === 0) swot.weaknesses.push('Market performance areas for improvement identified');
    if (swot.opportunities.length === 0) { swot.opportunities.push('Market expansion potential'); swot.opportunities.push('Operational efficiency improvements available'); }

    return swot;
  }

  function convertSwotToChartItems(swot: Record<string, string[]>): SwotItem[] {
    const items: SwotItem[] = [];
    swot.strengths.forEach(text => items.push({ text, impact: 7 + Math.random() * 3, category: 'strength' }));
    swot.weaknesses.forEach(text => items.push({ text, impact: 5 + Math.random() * 4, category: 'weakness' }));
    swot.opportunities.forEach(text => items.push({ text, impact: 4 + Math.random() * 5, category: 'opportunity' }));
    swot.threats.forEach(text => items.push({ text, impact: 3 + Math.random() * 6, category: 'threat' }));
    return items;
  }

  // --- Data fetching ---

  useEffect(() => {
    const fetchData = async () => {
      if (!valuationId || !supabase) return;

      setIsLoading(true);
      setHasError(false);
      setErrorMessage(null);

      try {
        const result = await retrieveValuationById(supabase, valuationId);

        if (!result.success || !result.data) {
          throw new Error(result.error || 'Failed to fetch valuation data');
        }

        const savedValuation = result.data;
        const savedData = savedValuation.data;
        const actualValue = getActualTotalValue(savedData);
        const displayVal = getDisplayValue(savedData);
        const totalGP = calculateTotalGrossProfit(savedData);

        const valuationProperty: ValuationProperty = {
          id: savedValuation.id,
          location: savedData.location || 'Property',
          date: savedValuation.lastModified,
          data: savedData,
          estimatedValue: {
            low: displayVal * 0.9,
            high: displayVal * 1.1,
            average: displayVal
          },
          metrics: {
            monthlyGallons: savedData.annualGallons ? savedData.annualGallons / 12 : 0,
            storeRevenue: savedData.storeRevenue || 0,
            storeSize: savedData.propertySize || 0,
            cstoreSalesPerSF: calculateSalesPerSF(savedData),
            fuelMargin: savedData.fuelMargin || 0,
            totalGrossProfit: totalGP
          },
          assets: {},
          hidden: false
        };

        setValuation(valuationProperty);

        // Fetch industry benchmarks
        const benchmarkResult = await fetchIndustryBenchmarks(supabase.client);
        if (benchmarkResult.success && benchmarkResult.data) {
          setIndustryBenchmarks(benchmarkResult.data);
        }

        const comparisonMetrics = generateFallbackComparisonMetrics(savedData);
        const swotAnalysis = generateSwot(savedData, comparisonMetrics);
        const swotChartItems = convertSwotToChartItems(swotAnalysis);

        setComparisonData(comparisonMetrics);
        setSwotItems(swotChartItems);
      } catch (err) {
        console.error('Error fetching valuation:', err);
        setHasError(true);
        setErrorMessage(err instanceof Error ? err.message : 'An unknown error occurred');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [valuationId, supabase]);

  // --- Format helpers ---
  const formatCurrency = (num: number) => {
    if (num >= 1000000) return `$${(num / 1000000).toFixed(2)}M`;
    if (num >= 1000) return `$${(num / 1000).toFixed(0)}K`;
    return `$${num.toLocaleString()}`;
  };

  // Breakdown panel uses the same nearest-$10K rounding as the post-payment
  // results page so the two views show identical numbers for the same valuation.
  const formatBreakdownCurrency = (num: number) => {
    const rounded = Math.round(num / 10000) * 10000;
    return `$${rounded.toLocaleString('en-US')}`;
  };

  const formatValue = (item: ComparisonData) => {
    if (item.isMonetary) return `$${item.userValue.toLocaleString()}${item.unit !== '$' ? ` ${item.unit}` : ''}`;
    return `${item.userValue.toLocaleString()} ${item.unit}`;
  };

  const formatAvg = (item: ComparisonData) => {
    if (item.isMonetary) return `$${item.average.toLocaleString()}${item.unit !== '$' ? ` ${item.unit}` : ''}`;
    return `${item.average.toLocaleString()} ${item.unit}`;
  };

  // --- Loading/Error states ---
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <span className="ml-3 text-gray-600">Loading valuation report...</span>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-red-800">Error Loading Report</h3>
        <p className="text-sm text-red-600 mt-1">{errorMessage}</p>
        <Link to="/dashboard/valuations" className="mt-4 inline-block text-sm text-blue-600 hover:text-blue-800 underline">
          Back to Valuations
        </Link>
      </div>
    );
  }

  if (!valuation) return null;

  const data = valuation.data!;
  const apiResult = data.apiResponse?.result;
  const displayVal = getDisplayValue(data);
  const costToCompete = calculateCostToCompete(data);
  const entrepreneurial = calculateEntrepreneurialValue(data, displayVal);
  const totalGP = calculateTotalGrossProfit(data);
  const opex = data.operatingExpenses || 0;
  const noi = totalGP - opex;

  // Category benchmark icons
  const categoryIcons: Record<string, React.ReactNode> = {
    'Annual Fuel Volume': <Fuel className="w-4 h-4 text-blue-600" />,
    'Fuel Profit Margin': <DollarSign className="w-4 h-4 text-green-600" />,
    'C-Store Sales': <ShoppingCart className="w-4 h-4 text-purple-600" />,
    'C-Store Sales per SF': <Building2 className="w-4 h-4 text-indigo-600" />,
    'Total Gross Profit': <PiggyBank className="w-4 h-4 text-emerald-600" />,
    'Operating Expenses': <Wallet className="w-4 h-4 text-red-600" />,
  };

  // Asset display mapping
  const assetDisplayMap: Record<string, { name: string; color: string }> = {
    'Convenience_Store': { name: 'Convenience Store', color: 'bg-green-500' },
    'Site_Improvements': { name: 'Site Improvements', color: 'bg-purple-500' },
    'FFE': { name: 'FF&E', color: 'bg-orange-500' },
    'Fueling_Equipment': { name: 'Fueling Equipment', color: 'bg-red-500' },
    'Fueling_Canopy': { name: 'Fueling Canopy', color: 'bg-yellow-500' },
    'UST': { name: 'Underground Storage Tanks', color: 'bg-indigo-500' },
    'Car_Wash': { name: 'Car Wash', color: 'bg-cyan-500' },
    'Car_Wash_Equipment': { name: 'Car Wash Equipment', color: 'bg-teal-500' },
    'Service_Station': { name: 'Service Station', color: 'bg-pink-500' },
    'Food_Booth': { name: 'Food Booth', color: 'bg-lime-500' },
    'Kiosk': { name: 'Kiosk', color: 'bg-amber-500' }
  };

  return (
    <div className="space-y-6">
      {/* Property Header — Satellite + Asset Breakdown */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          {/* Satellite Image */}
          <div className="relative bg-gray-100">
            <img
              src={getPropertySatelliteImageUrl(valuation.location, 600, 400, 18)}
              alt={`Satellite view of ${valuation.location}`}
              className="w-full h-full min-h-[300px] object-cover"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = 'https://via.placeholder.com/600x400/e5e7eb/6b7280?text=Map+Unavailable';
              }}
            />
            <div className="absolute bottom-3 left-3 bg-black/70 text-white px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" />
              Satellite View
            </div>
          </div>

          {/* Property Info + Asset Breakdown with Tabs */}
          <div className="p-6">
            <div className="mb-4">
              <h1 className="text-2xl font-bold text-gray-900">
                {valuation.location || 'Property Analysis'}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Valuation Report &middot; {valuation.date ? new Date(valuation.date).toLocaleDateString() : ''}
              </p>
            </div>

            {/* Total Value Banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-xs text-blue-600 font-semibold uppercase tracking-wider mb-1">Total Estimated Value</p>
              <p className="text-3xl font-bold text-blue-700">
                {formatCurrency(displayVal)}
              </p>
            </div>

            {/* Tab Switcher */}
            <div className="flex gap-1 mb-3">
              <button
                onClick={() => setActiveBreakdownTab('detailed')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  activeBreakdownTab === 'detailed' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Detailed Breakdown
              </button>
              <button
                onClick={() => setActiveBreakdownTab('input')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  activeBreakdownTab === 'input' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Input Summary
              </button>
            </div>

            {/* Detailed Breakdown Tab.
                Mirrors the post-payment results page exactly: same component
                values from `apiResponse.result`, same nearest-$10K rounding,
                and the residual business value is computed (not displayed raw)
                using the same formula as ValuationResult.tsx:
                  total_station_value − land − Σ depreciated_assets + fuelSupplyAdj */}
            {activeBreakdownTab === 'detailed' && (() => {
              const totalStationValue = apiResult?.total_station_value || 0;
              const landValue = apiResult?.predicted_land_value || 0;
              const depreciatedTotal = Object.values(apiResult?.depreciated_assets || {})
                .reduce((sum: number, asset: any) => sum + (asset?.Depreciated_Cost || 0), 0);
              const grossProfit = apiResult?.gross_profit || 0;
              const annualOpEx = data.isAnnual ? (data.operatingExpenses || 0) : (data.operatingExpenses || 0) * 12;
              const noi = grossProfit - annualOpEx;
              const fuelSupplyBenefit = calculateFuelSupplyBenefit(
                totalStationValue, grossProfit, noi,
                data.brandAffiliation || 'unbranded',
                data.fuelSupplyYears || 0
              );
              const fuelSupplyAdj = fuelSupplyBenefit.qualifies ? fuelSupplyBenefit.valuationAdjustment : 0;
              const residualBusinessValue = totalStationValue - landValue - depreciatedTotal + fuelSupplyAdj;

              return (
                <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
                  {apiResult?.predicted_land_value && (
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-blue-500 rounded-sm"></div>
                        <span className="text-sm text-gray-700">Land Value</span>
                      </div>
                      <span className="text-sm font-semibold text-gray-900">{formatBreakdownCurrency(apiResult.predicted_land_value)}</span>
                    </div>
                  )}

                  {apiResult?.depreciated_assets && Object.entries(apiResult.depreciated_assets)
                    .filter(([_, assetData]: [string, any]) => assetData && assetData.Depreciated_Cost > 0)
                    .sort(([,a]: [string, any], [,b]: [string, any]) => (b?.Depreciated_Cost || 0) - (a?.Depreciated_Cost || 0))
                    .map(([assetKey, assetData]: [string, any]) => {
                      if (!assetData) return null;
                      const displayInfo = assetDisplayMap[assetKey] || { name: assetKey.replace(/_/g, ' '), color: 'bg-gray-400' };
                      return (
                        <div key={assetKey} className="flex justify-between items-center py-2 border-b border-gray-100">
                          <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 ${displayInfo.color} rounded-sm`}></div>
                            <span className="text-sm text-gray-700">{displayInfo.name}</span>
                          </div>
                          <span className="text-sm font-semibold text-gray-900">{formatBreakdownCurrency(assetData.Depreciated_Cost)}</span>
                        </div>
                      );
                    })}

                  {residualBusinessValue > 0 && (
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-emerald-500 rounded-sm"></div>
                        <span className="text-sm text-gray-700">Residual Business Value</span>
                      </div>
                      <span className="text-sm font-semibold text-gray-900">{formatBreakdownCurrency(residualBusinessValue)}</span>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Input Summary Tab */}
            {activeBreakdownTab === 'input' && (
              <div className="space-y-1 max-h-[280px] overflow-y-auto pr-1 text-sm">
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                  {data.propertySize && <><span className="text-gray-500">Building Size:</span><span className="font-medium">{data.propertySize.toLocaleString()} sq ft</span></>}
                  {data.lotSize && <><span className="text-gray-500">Lot Size:</span><span className="font-medium">{data.lotSize.toLocaleString()} sq ft</span></>}
                  {data.canopySize && <><span className="text-gray-500">Canopy:</span><span className="font-medium">{data.canopySize.toLocaleString()} sq ft</span></>}
                  {data.yearBuilt && <><span className="text-gray-500">Year Built:</span><span className="font-medium">{data.yearBuilt}</span></>}
                  {data.numberOfPumps && <><span className="text-gray-500">Pumps:</span><span className="font-medium">{data.numberOfPumps}</span></>}
                  {data.tankCapacity && <><span className="text-gray-500">UST Capacity:</span><span className="font-medium">{data.tankCapacity.toLocaleString()} gal</span></>}
                  {data.brandName && <><span className="text-gray-500">Fuel Brand:</span><span className="font-medium">{data.brandName}</span></>}
                  {data.annualGallons && <><span className="text-gray-500">Annual Gallons:</span><span className="font-medium">{data.annualGallons.toLocaleString()}</span></>}
                  {data.fuelMargin && <><span className="text-gray-500">Fuel Margin:</span><span className="font-medium">${data.fuelMargin.toFixed(2)}/gal</span></>}
                  {data.storeRevenue && <><span className="text-gray-500">Store Revenue:</span><span className="font-medium">${data.storeRevenue.toLocaleString()}</span></>}
                  {data.operatingExpenses && <><span className="text-gray-500">Operating Expenses:</span><span className="font-medium">${data.operatingExpenses.toLocaleString()}</span></>}
                  {data.operatingHours && <><span className="text-gray-500">Hours:</span><span className="font-medium">{data.operatingHours}</span></>}
                  {data.hasCarWash && <><span className="text-gray-500">Car Wash:</span><span className="font-medium">{data.carWashType || 'Yes'}</span></>}
                  {data.carWashRevenue && <><span className="text-gray-500">Car Wash Rev:</span><span className="font-medium">${data.carWashRevenue.toLocaleString()}</span></>}
                  {data.hasFoodService && <><span className="text-gray-500">Food Service:</span><span className="font-medium">Yes</span></>}
                  {data.foodServiceRevenue && <><span className="text-gray-500">Food Service Rev:</span><span className="font-medium">${data.foodServiceRevenue.toLocaleString()}</span></>}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 3-Column Valuation Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Cost to Compete */}
        <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-5">
          <h3 className="text-sm font-semibold text-orange-800 mb-2">Cost to Compete</h3>
          <p className="text-2xl font-bold text-orange-600">
            {costToCompete > 0 ? formatCurrency(costToCompete) : 'N/A'}
          </p>
          <p className="text-xs text-orange-700 mt-1">RCN + 25% indirect costs, less 20% entrepreneurial incentive</p>
        </div>

        {/* Estimated Property Value */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-5">
          <h3 className="text-sm font-semibold text-blue-800 mb-2">Estimated Property Value</h3>
          <p className="text-2xl font-bold text-blue-600">{formatCurrency(displayVal)}</p>
          <p className="text-xs text-blue-700 mt-1">Current market value assessment</p>
        </div>

        {/* Entrepreneurial Value */}
        <div className="bg-green-50 border-2 border-green-200 rounded-lg p-5">
          <h3 className="text-sm font-semibold text-green-800 mb-2">Entrepreneurial Value</h3>
          <p className="text-2xl font-bold text-green-600">{formatCurrency(entrepreneurial.value)}</p>
          <p className="text-xs text-green-700 mt-1">
            {entrepreneurial.factorCount > 0
              ? `Operational uplift (+${(entrepreneurial.uplift * 100).toFixed(1)}%)`
              : 'Default 15% operational uplift'}
          </p>
        </div>
      </div>

      {/* Category-Based Performance Benchmarks */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-semibold text-gray-900">Performance Benchmarks</h2>
        </div>

        {/* Shared legend — explains the markers used inside each card. */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-gray-600 mb-4">
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-blue-500 border-2 border-white shadow inline-block"></span>
            <span>Your Property</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-0.5 h-4 bg-gray-700 inline-block"></span>
            <span>Industry Average</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-0.5 h-4 bg-emerald-600 inline-block"></span>
            <span>Top Performer</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-6 h-1.5 rounded-full bg-gradient-to-r from-red-200 via-yellow-200 via-green-200 to-blue-200"></span>
            <span>Industry Range (low → high)</span>
          </div>
        </div>

        {/* Category Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {comparisonData.map((item, index) => {
            const inversed = item.desiredTrend === 'down';
            const indicator = getPerformanceIndicator(item.percentileRank, inversed);
            const icon = categoryIcons[item.metricName] || <BarChart3 className="w-4 h-4 text-gray-500" />;

            let indicatorColor = 'text-gray-500';
            switch (indicator) {
              case 'Excellent': indicatorColor = 'text-green-600'; break;
              case 'Good': indicatorColor = 'text-blue-600'; break;
              case 'Average': indicatorColor = 'text-yellow-600'; break;
              case 'Below Average': indicatorColor = 'text-red-600'; break;
            }

            return (
              <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-center gap-2 mb-3">
                  {icon}
                  <h4 className="text-sm font-semibold text-gray-800">{item.metricName}</h4>
                </div>

                {/* Compact range bar.
                    Shows where this metric's value sits on the 0–100 percentile
                    scale relative to the industry average and top-performer
                    threshold. Replaces a Chart.js bar chart that didn't render
                    legibly at this height — Chart.js can't fit four overlapping
                    datasets in <100px and the markers were getting clipped. */}
                {(() => {
                  const benchmark = industryBenchmarks.find(b => b.metricName === item.metricName);
                  const normalize = (v: number) => {
                    if (!benchmark || benchmark.industryMax === benchmark.industryMin) return 50;
                    return Math.max(0, Math.min(100,
                      ((v - benchmark.industryMin) / (benchmark.industryMax - benchmark.industryMin)) * 100
                    ));
                  };
                  const avgPos = benchmark ? normalize(benchmark.nationalAverage) : 50;
                  const topPos = benchmark?.topPerformerThreshold ? normalize(benchmark.topPerformerThreshold) : null;
                  const userPos = Math.max(0, Math.min(100, item.percentileRank));
                  const inversedHere = item.desiredTrend === 'down';
                  const userMarkerColor =
                    indicator === 'Excellent' ? 'bg-green-500'
                    : indicator === 'Good' ? 'bg-blue-500'
                    : indicator === 'Average' ? 'bg-yellow-500'
                    : 'bg-red-500';
                  const gradient = inversedHere
                    ? 'bg-gradient-to-r from-blue-200 via-green-200 via-yellow-200 to-red-200'
                    : 'bg-gradient-to-r from-red-200 via-yellow-200 via-green-200 to-blue-200';
                  return (
                    <div className="mb-3 mt-1">
                      <div className="relative h-7">
                        {/* Range gradient */}
                        <div className={`absolute top-3 left-0 right-0 h-1.5 rounded-full ${gradient}`}></div>
                        {/* Industry Average marker */}
                        <div
                          className="absolute top-1 w-0.5 h-5 bg-gray-700"
                          style={{ left: `${avgPos}%` }}
                          title={`Industry Avg (${avgPos.toFixed(0)}th percentile)`}
                        ></div>
                        {/* Top Performer marker */}
                        {topPos !== null && (
                          <div
                            className="absolute top-1 w-0.5 h-5 bg-emerald-600"
                            style={{ left: `${topPos}%` }}
                            title={`Top Performer (${topPos.toFixed(0)}th percentile)`}
                          ></div>
                        )}
                        {/* Your value marker */}
                        <div
                          className={`absolute top-0.5 w-3 h-6 rounded-full border-2 border-white ${userMarkerColor} shadow`}
                          style={{ left: `calc(${userPos}% - 6px)` }}
                          title={`Your Property (${userPos.toFixed(0)}th percentile)`}
                        ></div>
                      </div>
                      <div className="flex justify-between text-[10px] text-gray-500 mt-1 px-0.5">
                        <span>0</span>
                        <span>25</span>
                        <span>50 (avg)</span>
                        <span>75</span>
                        <span>100</span>
                      </div>
                    </div>
                  );
                })()}

                <div className="flex justify-between items-end text-xs">
                  <div>
                    <span className="text-gray-500">Your Value</span>
                    <p className="font-semibold text-gray-900">{formatValue(item)}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-gray-500">Industry Avg</span>
                    <p className="font-medium text-gray-600">{formatAvg(item)}</p>
                  </div>
                  <div className="text-right">
                    <span className={`font-semibold ${indicatorColor}`}>{indicator}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Comparison Table */}
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Metric</th>
                <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Your Value</th>
                <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Industry Avg</th>
                <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Performance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {comparisonData.map((item, index) => {
                const inversed = item.desiredTrend === 'down';
                const indicator = getPerformanceIndicator(item.percentileRank, inversed);
                let iconColor = '';
                let Icon = TrendingUp;
                switch (indicator) {
                  case 'Excellent': iconColor = 'text-green-500'; Icon = inversed ? TrendingDown : TrendingUp; break;
                  case 'Good': iconColor = 'text-blue-500'; Icon = inversed ? TrendingDown : TrendingUp; break;
                  case 'Average': iconColor = 'text-yellow-500'; Icon = Zap; break;
                  case 'Below Average': iconColor = 'text-red-500'; Icon = AlertTriangle; break;
                }
                return (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm font-medium text-gray-900">{item.metricName}</td>
                    <td className="py-3 px-4 text-sm text-gray-700">{formatValue(item)}</td>
                    <td className="py-3 px-4 text-sm text-gray-500">{formatAvg(item)}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Icon className={`w-4 h-4 ${iconColor}`} />
                        <span className="text-sm font-medium">{indicator}</span>
                        <div className="flex-1 max-w-[80px] bg-gray-200 h-1.5 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${getPercentileColor(item.percentileRank, inversed)}`}
                            style={{ width: `${item.percentileRank}%` }}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* SWOT Analysis */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <FileText className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-semibold text-gray-900">SWOT Analysis</h2>
        </div>

        <div className="bg-gray-50 rounded-lg p-4 mb-4" style={{ height: '350px' }}>
          <SwotQuadrantChart swotItems={swotItems} width={800} height={320} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <h4 className="font-semibold text-green-800 mb-2 text-xs uppercase tracking-wider">Strengths</h4>
            <ol className="list-decimal pl-4 space-y-1 text-sm text-green-900">
              {swotItems.filter(item => item.category === 'strength').map((item, index) => (
                <li key={index}>{item.text}</li>
              ))}
            </ol>
          </div>
          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
            <h4 className="font-semibold text-red-800 mb-2 text-xs uppercase tracking-wider">Weaknesses</h4>
            <ol className="list-decimal pl-4 space-y-1 text-sm text-red-900">
              {swotItems.filter(item => item.category === 'weakness').map((item, index) => (
                <li key={index}>{item.text}</li>
              ))}
            </ol>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h4 className="font-semibold text-blue-800 mb-2 text-xs uppercase tracking-wider">Opportunities</h4>
            <ol className="list-decimal pl-4 space-y-1 text-sm text-blue-900">
              {swotItems.filter(item => item.category === 'opportunity').map((item, index) => (
                <li key={index}>{item.text}</li>
              ))}
            </ol>
          </div>
          <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
            <h4 className="font-semibold text-amber-800 mb-2 text-xs uppercase tracking-wider">Threats</h4>
            <ol className="list-decimal pl-4 space-y-1 text-sm text-amber-900">
              {swotItems.filter(item => item.category === 'threat').map((item, index) => (
                <li key={index}>{item.text}</li>
              ))}
            </ol>
          </div>
        </div>
      </div>

      {/* Tax Depreciation & Loan Calculator — Side by Side on Desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tax Depreciation Analysis */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Calculator className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">Tax Depreciation</h2>
          </div>
          <TaxDepreciationDemo totalPropertyValue={displayVal} />
        </div>

        {/* Loan Value Analysis */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <DollarSign className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">Loan Analysis</h2>
          </div>
          <LoanCalculatorDemo
            totalPropertyValue={displayVal}
            goingConcernNOI={noi > 0 ? noi : undefined}
            businessValue={apiResult?.predicted_business_value}
            ffeValue={apiResult?.depreciated_assets?.FFE?.Depreciated_Cost}
          />
        </div>

        {/* Next Steps — mirrors the post-payment results page */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Next Steps</h2>
          <p className="text-gray-600 mb-6">
            Ready to explore your options? Connect with trusted professionals to take the next step in your gas station journey.
          </p>
          <div className="flex flex-wrap lg:flex-nowrap gap-4">
            <Button
              id="connect-broker-button"
              onClick={() => setBrokerModalOpen(true)}
              className="flex items-center gap-2 px-6 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
            >
              <Users className="w-5 h-5" />
              <span>Connect with a Local Broker</span>
            </Button>
            <Button
              id="connect-lender-button"
              disabled={true}
              className="flex items-center gap-2 px-6 py-2 rounded-lg bg-purple-600 text-white cursor-not-allowed opacity-60"
            >
              <DollarSign className="w-5 h-5" />
              <span>Connect with a Lender <span className="text-purple-200 text-xs">(Coming Soon)</span></span>
            </Button>
          </div>
        </div>
      </div>

      <BrokerConnectionModal
        isOpen={brokerModalOpen}
        onClose={() => setBrokerModalOpen(false)}
        propertyAddress={data.location || valuation?.location || ''}
        stationObjectId={String(data.stationObjectId || '')}
      />
    </div>
  );
}
